/**
 * AiChar · 场景注册表（新增场景的唯一改动点）
 * ------------------------------------------------------------
 * 角色的每个「活动场景」是 scenes/ 下一个自包含组件（自带舞台、动画、台词），
 * 在这份注册表里登记一行即可生效——不需要改 AiCharacter 外壳和任何业务代码。
 *
 * 新增一个小场景的步骤：
 *   1. 在 scenes/ 下新建 SceneXxx.vue（舞台随意设计：窗台、工位、白板、走廊……）；
 *   2. 在下面 AI_SCENES 对应状态的数组里加一条：{ comp, lines, ... }；
 *   3. 若要挂在新的在线状态上，在 portal.ts 的 Presence 联合类型里补一个状态值。
 * 未登记的状态回落到 online 场景组。
 *
 * 【尺寸契约】所有场景的人物必须一样大，新增场景务必遵守：
 *   - 渲染比例统一 0.8px / viewBox 单位（SceneSleep 躺姿例外，0.9）；
 *   - 站立/坐姿人物头一律 r=6.6（Sleep 躺姿 r=5.9），头径 ≈10.6px；
 *   - 面部特征（发/眉/眼/腮红/嘴）直接按 SceneOnline 的坐标平移复用。
 *
 * 【多变体】每个状态可挂多个场景，rollScene 随机抽取（避开当前正在演的），
 * 状态切换或外壳定时轮换时生效——同一状态每次登场都可能有新花样。
 */
import type { AiScene } from '../types'
import SceneOnline from './SceneOnline.vue'
import ScenePaperPlane from './ScenePaperPlane.vue'
import SceneBusy from './SceneBusy.vue'
import SceneMeeting from './SceneMeeting.vue'
import SceneBoxes from './SceneBoxes.vue'
import SceneAway from './SceneAway.vue'
import SceneCoffee from './SceneCoffee.vue'
import SceneSleep from './SceneSleep.vue'

export const AI_SCENES: Record<string, AiScene[]> = {
  /* 在线：窗台上玩（探头看你干活） / 窗台上扔纸飞机 */
  online: [
    {
      comp: SceneOnline,
      peekable: true,
      peekLines: [
        '让我瞅瞅你在干嘛',
        '探头看看你干活～',
        '认真干，我盯着你',
        '哟，进展怎么样？',
        '需要我搭把手不？'
      ],
      lines: [
        '在的呢～',
        '点我一下试试',
        '嘿嘿，被发现了',
        '需要帮忙吗？',
        '今天辛苦啦！',
        '我在这儿偷看呢',
        '有活儿喊我一声',
        '陪你摸摸鱼～'
      ]
    },
    {
      comp: ScenePaperPlane,
      lines: [
        '纸飞机已空投，请查收',
        '飞咯～看看它去哪儿',
        '它替我巡场呢',
        '这封邮件不用回',
        '哈，完美弧线！',
        '接着——'
      ]
    }
  ],
  /* 忙碌：工位打字赶工 / 白板前讲方案 / 搬砖 */
  busy: [
    {
      comp: SceneBusy,
      lines: [
        '别催啦，在赶工呢',
        '这方案改到第 8 版了',
        '先把这封邮件回完……',
        '敲字中，勿扰哈',
        '忙完这阵就来陪你'
      ]
    },
    {
      comp: SceneMeeting,
      lines: [
        '都安静，听我讲两句',
        '这个方案我觉得行',
        '划重点了啊',
        'Q3 目标，稳！',
        '谁还有问题？',
        '数据说明一切'
      ]
    },
    {
      comp: SceneBoxes,
      lines: [
        '搬砖中，勿扰',
        '这箱需求先搬走',
        '今天也是充实的一天',
        '搬完这波就下班',
        '我搬的不是砖，是梦想'
      ]
    }
  ],
  /* 离开：走廊散步 / 端着咖啡溜达 */
  away: [
    {
      comp: SceneAway,
      lines: [
        '我出去遛个弯儿',
        '倒杯水，马上回来',
        '去隔壁串个门',
        '伸个懒腰先～',
        '散散步，思路更清楚'
      ]
    },
    {
      comp: SceneCoffee,
      lines: [
        '咖啡续命中……',
        '茶水间溜达一圈',
        '第三杯了，别学我',
        '端着咖啡思考人生',
        '美式，不加糖，加命'
      ]
    }
  ],
  /* 隐身（offline）：夜窗下盖被子睡觉（说话稀一些，别吵醒它） */
  offline: [
    {
      comp: SceneSleep,
      gap: [15000, 27000],
      lines: [
        'Zzz……',
        '呼……呼……',
        '嘘……人家睡了啦',
        '（说梦话）明天再做……',
        '晚安，梦里见～'
      ]
    }
  ]
}

/**
 * 按状态随机抽一个场景；exclude 传入当前场景时避开它，
 * 保证多场景状态下轮换一定换新花样。未登记的状态回落 online 组。
 */
export function rollScene(id: string, exclude?: AiScene): AiScene {
  const list = AI_SCENES[id] ?? AI_SCENES.online
  const pool = exclude && list.length > 1 ? list.filter(s => s !== exclude) : list
  return pool[Math.floor(Math.random() * pool.length)]
}
